import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useRoleCheck, type RolesConfig } from '@/hooks/useRoleCheck';

export interface RoleBasedRouteProps {
  /** Role requirements configuration */
  roles?: RolesConfig;
  /** Optional explicit organization ID (overrides route params) */
  organizationId?: string;
  /** Content to render if user has access */
  children: ReactNode;
  /** Optional redirect path if access is denied (default: '/') */
  redirectTo?: string;
  /** Optional loading component */
  loadingComponent?: ReactNode;
  /** Optional fallback component when access is denied (instead of redirecting) */
  fallbackComponent?: ReactNode;
}

/**
 * Route wrapper that only renders children if user has required roles
 *
 * Supports both global roles and organization-scoped roles.
 * Automatically reads organizationId from route params if roles.param is specified.
 *
 * @example
 * // Global role check
 * <RoleBasedRoute roles={{ anyOf: ['user'] }}>
 *   <ProtectedPage />
 * </RoleBasedRoute>
 *
 * @example
 * // Organization role check using route params
 * <Route path="/organizations/:organizationId/settings" element={
 *   <RoleBasedRoute roles={{ param: 'organizationId', anyOf: ['owner', 'manager'] }}>
 *     <OrganizationSettings />
 *   </RoleBasedRoute>
 * } />
 *
 * @example
 * // With custom redirect
 * <RoleBasedRoute
 *   roles={{ anyOf: ['owner'] }}
 *   redirectTo="/access-denied"
 * >
 *   <AdminPanel />
 * </RoleBasedRoute>
 *
 * @example
 * // With fallback component instead of redirect
 * <RoleBasedRoute
 *   roles={{ param: 'organizationId', anyOf: ['owner'] }}
 *   fallbackComponent={<AccessDenied />}
 * >
 *   <OrganizationDangerZone />
 * </RoleBasedRoute>
 */
export function RoleBasedRoute({
  roles,
  organizationId,
  children,
  redirectTo = '/',
  loadingComponent = (
    <div className="min-h-screen grid place-items-center">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  ),
  fallbackComponent,
}: RoleBasedRouteProps) {
  const { hasAccess, isLoading } = useRoleCheck(roles, organizationId);

  if (isLoading) {
    return <>{loadingComponent}</>;
  }

  if (!hasAccess) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
